import { Box, Theme } from "@outschool/backpack";
import { Search } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import {
  TrackedButton,
  TrackedButtonProps,
  useFeatureFlag
} from "@outschool/ui-components-shared";
import { Screen, useIsMobile } from "@outschool/ui-utils";
import React from "react";

// A subset of SearchFiltersType is used to determine which toggle button / tab is selected.
export type SearchToggleFiltersType = Pick<
  SearchFiltersType,
  "capacityMin" | "capacityMax" | "delivery" | "includedWithMembership"
>;

type SearchToggleButtonsProps = {
  searchFilters: SearchFiltersType;
  onChange: (searchToggleFilters: SearchToggleFiltersType) => void;
  clearDropdowns: () => void;
};

export default function SearchToggleButtons({
  searchFilters,
  onChange,
  clearDropdowns
}: SearchToggleButtonsProps) {
  const { t } = useTranslation(
    "client\\components\\search\\SearchToggleButtons"
  );
  const isFreeSelfPacedEnabled = useFeatureFlag(
    "self-paced-included-with-membership"
  );

  const handleFilterChange = (searchFilters: SearchFiltersType) => {
    clearDropdowns();
    onChange(searchFilters);
  };

  const isMobile = useIsMobile();

  const activeSearchToggle =
    Search.activeSearchToggleFromFilters(searchFilters);

  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        gap: "0.5em",
        overflowX: "auto",
        "&::-webkit-scrollbar": {
          display: "none" // Chrome, Safari, Edge
        },
        [theme.breakpoints.down("lg")]: {
          paddingX: "1em"
        }
      })}
    >
      <SquareButton
        isSelected={activeSearchToggle === Search.SearchToggleType.ALL}
        onClick={() => handleFilterChange(Search.SEARCH_TOGGLE_TO_FILTERS.all)}
        toggleName={Search.SearchToggleType.ALL}
      >
        {t`All`}
      </SquareButton>
      {isFreeSelfPacedEnabled && (
        <SquareButton
          isSelected={
            activeSearchToggle ===
            Search.SearchToggleType.INCLUDED_WITH_MEMBERSHIP
          }
          onClick={() =>
            handleFilterChange(
              Search.SEARCH_TOGGLE_TO_FILTERS[
                Search.SearchToggleType.INCLUDED_WITH_MEMBERSHIP
              ]
            )
          }
          toggleName={Search.SearchToggleType.INCLUDED_WITH_MEMBERSHIP}
        >
          {t`Free for Members`}
        </SquareButton>
      )}
      <SquareButton
        isSelected={activeSearchToggle === Search.SearchToggleType.COURSES}
        onClick={() => {
          handleFilterChange(Search.SEARCH_TOGGLE_TO_FILTERS.courses);
        }}
        toggleName={Search.SearchToggleType.COURSES}
      >
        {isMobile ? t`Courses` : t`Full Courses`}
      </SquareButton>
      <SquareButton
        isSelected={activeSearchToggle === Search.SearchToggleType.TUTORING}
        onClick={() =>
          handleFilterChange(Search.SEARCH_TOGGLE_TO_FILTERS.tutoring)
        }
        toggleName={Search.SearchToggleType.TUTORING}
      >
        {isMobile ? t`Tutoring` : t`Tutoring & Private Lessons`}
      </SquareButton>
      <SquareButton
        isSelected={activeSearchToggle === Search.SearchToggleType.CLUBS}
        onClick={() => {
          handleFilterChange(Search.SEARCH_TOGGLE_TO_FILTERS.clubs);
        }}
        toggleName={Search.SearchToggleType.CLUBS}
      >
        {isMobile ? t`Enrichment` : t`Enrichment & Clubs`}
      </SquareButton>
      <SquareButton
        isSelected={activeSearchToggle === Search.SearchToggleType.ONE_TIME}
        onClick={() =>
          handleFilterChange(Search.SEARCH_TOGGLE_TO_FILTERS["one-time"])
        }
        toggleName={Search.SearchToggleType.ONE_TIME}
      >
        {isMobile ? t`One-Time` : t`One-Time Activities`}
      </SquareButton>
    </Box>
  );
}

interface SquareButtonProps extends TrackedButtonProps {
  isSelected?: boolean;
  toggleName: string;
}

export function SquareButton({
  isSelected,
  children,
  toggleName,
  ...buttonProps
}: SquareButtonProps) {
  const isMobile = Screen.useIsMobile();
  return (
    <TrackedButton
      variant="contained"
      size="small"
      sx={{
        borderRadius: "0.5em",
        boxShadow: "none",
        fontWeight: "500",
        fontSize: isMobile ? "14px" : "16px",
        color: "#262626",
        backgroundColor: "primary.200",
        "&:hover, &:focus, &:active": {
          color: isMobile ? "#FFFFFF !important" : "#262626 !important",
          backgroundColor: isMobile
            ? "#6161F2 !important"
            : "#B4BEFE !important"
        },
        ":focus:not(:focus-visible)": {
          color: "#FFFFFF !important",
          backgroundColor: "#6161F2 !important"
        },
        ...(isSelected && { color: "#FFFFFF", backgroundColor: "#6161F2" })
      }}
      trackingName={"search_toggle"}
      {...buttonProps}
      trackingSharedProperties={{
        ...buttonProps.trackingSharedProperties,
        toggleName
      }}
    >
      {children}
    </TrackedButton>
  );
}
